import { parseWizardNav } from '../api/webshopConfig';
import type {
    Component,
    CombinedImageMode,
    Edition,
    GroupLayout,
    PriceDisplayType,
    RequiresBundle,
} from '../schema/component';
import type { ComponentSetting } from '../schema/setting';
import type { V3ComponentResponse } from '../api/types';
import { parseGroupArray } from './parseGroup';
import { parseRuleArray, parseRuleGroupArray } from './parseRule';
import { parseCustomFieldDefinitions } from './parseCustomFields';
import { boolFromStringFlag, intFromMixed, localized, numericString, optionalString, withDefaultLocale } from './coerce';

/**
 * Entry-point: convert the wire-format V3 envelope into a fully-typed
 * {@link Component}. Tolerant of legacy quirks (boolean-as-string,
 * mixed-id types, missing/empty localised strings, AngularJS leftovers).
 */
export function parseComponent(response: V3ComponentResponse): Component {
    const data = (response.data ?? {}) as Record<string, unknown>;
    const setting = parseSetting((response.setting ?? {}) as Record<string, unknown>);
    // '0' is the admin's "Use global settings" — fall back to the
    // account-wide default the settings carry.
    const defaultLocale = componentLocale(data.default_locale) ?? setting.defaultLocale;

    return withDefaultLocale(defaultLocale, () => ({
        id: intFromMixed(data.id ?? response.componentId, 0),
        title: localized(data.title),
        headline: localized(data.headline),
        defaultLocale,

        frontendVersion: optionalString(data.frontend_version) ?? response.frontendVersion ?? undefined,
        edition: parseEdition(response.edition),
        requiresBundle: parseRequiresBundle(data.requires_bundle),
        cmsBlockRefs: Array.isArray(data.cms_block_refs)
            ? data.cms_block_refs.filter((x): x is string => typeof x === 'string')
            : [],

        layout: parseLayout(data.groupLayout),
        groupWizard: boolFromStringFlag(data.groupWizard),
        ...(parseWizardNav(data.groupWizardNav) ? { wizardNav: parseWizardNav(data.groupWizardNav) } : {}),
        groupWizardAutoscroll: boolFromStringFlag(data.groupWizardAutoscroll),
        groupScrollOffset: intFromMixed(data.groupScrollOffset, 0),
        valueImagePosition: typeof data.optionImagePosition === 'string'
            ? data.optionImagePosition
            : 'left',
        combinedImage: parseCombinedImage(data.combinedimage),

        displayPrice: boolFromStringFlag(data.displayPrice),
        displayPriceType: data.displayPriceType === 'relative' ? 'relative' : 'absolute' as PriceDisplayType,
        displayPriceIfZero: boolFromStringFlag(data.displayPriceIfZero),
        displayPricePattern: optionalString(data.displayPricePattern),
        // Component-level overrides the account-wide setting, but only when the
        // wire actually ships the field on `data`. Some Component snapshots
        // omit it entirely and rely on the account default — falling back to
        // the setting in that case avoids defaulting to `false` (= "excl.
        // tax") on every shop whose admin checkbox wasn't re-saved.
        priceIncludesTax: resolvePriceIncludesTax(data, response.setting),

        showMinMax: boolFromStringFlag(data.showMinMax),
        minMaxPattern: optionalString(data.minMaxPattern),
        showCombinedSku: boolFromStringFlag(data.showCombinedSku),

        country: optionalString(data.country),
        massFactor: optionalString(data.mass_factor),
        altCheckout: boolFromStringFlag(data.alt_checkout),
        svgExport: boolFromStringFlag(data.svg_export),
        customCss: optionalString(data.customCss),
        customJavascript: optionalString(data.customJavascript),
        activeColor: optionalString(data.activeColor),
        brandColor: hexColor(response.brandColor),
        loadingAnimationColor: optionalString(data.loadingAnimationColor),

        groups: parseGroupArray(data.option_groups),
        rules: parseRuleArray(data.rules),
        ruleGroups: parseRuleGroupArray(data.rulesgroups),
        customFieldDefinitions: parseCustomFieldDefinitions(data.customFields),

        setting,
    }));
}

function resolvePriceIncludesTax(
    data: Record<string, unknown>,
    setting: unknown,
): boolean {
    if (data.priceIncludesTax !== undefined && data.priceIncludesTax !== null) {
        return boolFromStringFlag(data.priceIncludesTax);
    }
    const s = (setting ?? {}) as Record<string, unknown>;
    if (s.priceIncludesTax !== undefined && s.priceIncludesTax !== null) {
        return boolFromStringFlag(s.priceIncludesTax);
    }
    // Sensible storefront default — most webshops display gross prices.
    return true;
}

function parseLayout(v: unknown): GroupLayout {
    if (v === 'no_groups' || v === 'tabs' || v === 'wizard') return v;
    return 'no_groups';
}

function parseCombinedImage(v: unknown): CombinedImageMode {
    if (v === 'combined-product-image' || v === 'combined-product-image-360') return v;
    // Legacy admin sometimes stores the mode as a truthy short-form
    // ("1", 1, true) and only writes the descriptive string after a
    // re-save. Treat any non-empty truthy value as the static-stack
    // mode so components imported from the legacy cloud still render
    // their combined image without a re-save.
    if (v === 'no' || v === '0' || v === 0 || v === false || v == null || v === '') {
        return 'no';
    }
    return 'combined-product-image';
}

function parseEdition(v: unknown): Edition {
    if (v === 'BASIC' || v === 'PROFESSIONAL' || v === 'ULTIMATE') return v;
    return 'PROFESSIONAL';
}

function parseRequiresBundle(v: unknown): RequiresBundle {
    if (v === 'v3') return 'v3';
    return 'legacy';
}

function parseSetting(s: Record<string, unknown>): ComponentSetting {
    const dp = (s.data_protection ?? {}) as Record<string, unknown>;
    return {
        // Wire format ships taxRate as a number (e.g. `19`), not a string.
        // optionalString would drop it on the floor → configuratorTaxRate=0
        // → tax conversion would treat configurator-prices as net and add
        // store-tax on top (980 € becoming 1166.20 €). numericString keeps
        // the number coerced to a string for downstream parseTaxRate.
        taxRate: numericString(s.taxRate),
        selectedLanguages: Array.isArray(s.selectedLanguages)
            ? s.selectedLanguages.filter((x): x is string => typeof x === 'string')
            : [],
        defaultLocale: optionalString(s.default_locale),
        defaultCurrencyCode: optionalString(s.default_currency_code),
        system: optionalString(s.system),
        matrixDelimiter: optionalString(s.matrix_delimiter),
        priceIncludesTax: s.priceIncludesTax != null
            ? boolFromStringFlag(s.priceIncludesTax)
            : undefined,
        dataProtection: Object.keys(dp).length > 0
            ? {
                name: optionalString(dp.name),
                version: optionalString(dp.version),
                date: optionalString(dp.date),
            }
            : undefined,
    };
}

function componentLocale(raw: unknown): string | undefined {
    const value = optionalString(raw);
    return value === '0' ? undefined : value;
}

/** `#rrggbb` or undefined — the value lands in an inline style. */
function hexColor(v: unknown): string | undefined {
    return typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v) ? v : undefined;
}
